import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { OutsideLink } from '@openx/components-core/lib/Link/Link.tsx';
import { Logo } from '@openx/components-core/lib/Logo/Logo.tsx';
import { OpenXLogo } from '@openx/components-core/lib/Logo/OpenXLogo.tsx';
import { WEIGHT_EXTRABOLD, WEIGHT_NORMAL } from '@openx/components-core/lib/theme/typography.ts';

const styles: { [key: string]: SxProps<Theme> } = {
  centeredContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  copyright: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 50px',
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  promoContainer: {
    background: (theme) => theme.palette.secondary.main,
    color: (theme) => theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 50px',
  },
  rootContainer: {
    backgroundColor: (theme) => theme.palette.background.paper,
    flex: 1,
    minHeight: 0,
  },
  subtitle: {
    fontSize: 18,
    fontWeight: 'fontWeightRegular',
    lineHeight: '26px',
  },
};

const EXTERNAL_LINKS: { title: string; link: string; dataTest: string }[] = [
  {
    dataTest: 'terms-and-conditions',
    link: 'https://www.openx.com/legal/terms-and-conditions',
    title: 'Terms and Conditions',
  },
  {
    dataTest: 'privacy',
    link: 'https://www.openx.com/legal/privacypolicy',
    title: 'Privacy Policy',
  },
  {
    dataTest: 'about',
    link: 'https://www.openx.com/about',
    title: 'About',
  },
  {
    dataTest: 'help',
    link: 'https://www.openx.com/support',
    title: 'Help',
  },
];

export function SsoLayout({ children }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: 'center',
          alignItems: 'stretch',
          ...styles.rootContainer,
        }}
      >
        <Grid size={4} sx={styles.formContainer}>
          <Box>
            <Logo data-test="logo" />
          </Box>
          <Box sx={styles.centeredContainer}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '24px' }}>
              <OpenXLogo />
              <Typography variant="h1" sx={{ color: (theme) => theme.palette.secondary.main }}>
                OpenX Platform
              </Typography>
            </Box>
            {children}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {EXTERNAL_LINKS.map(({ title, link, dataTest }, index) => (
              <OutsideLink
                key={index}
                to={link}
                openInNewTab={true}
                data-test={dataTest}
                sx={{
                  padding: '4px',
                  fontSize: 12,
                  fontWeight: 'fontWeightBold',
                  textTransform: 'uppercase',
                  color: (theme) => theme.palette.text.secondary,
                }}
              >
                {title}
              </OutsideLink>
            ))}
          </Box>
        </Grid>
        <Grid size={8} sx={styles.promoContainer}>
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center', gap: '24px' }}>
            <Typography
              variant="h1"
              sx={{ fontSize: 44, fontWeight: WEIGHT_EXTRABOLD, lineHeight: '60px' }}
              data-test="advert-text"
            >
              Simplify advertising with The Intelligent SSP™
            </Typography>
            <Typography
              data-test="advert-subtext"
              variant="h2"
              sx={{ fontSize: 20, fontWeight: WEIGHT_NORMAL, lineHeight: '28px', '&:before': { display: 'none' } }}
            >
              Solve complex business needs with direct access to quality media, privacy-forward targeting, and
              future-ready infrastructure built to perform.
            </Typography>
          </Box>
          <Box sx={styles.copyright}>
            <Typography variant="body2">Copyright © {new Date().getFullYear()} OpenX. All rights reserved.</Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
